'use client';

import { useContext, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import type { FormikHelpers } from 'formik';

import { Form } from 'src/shared/components/form';
import { Typography } from 'src/shared/ui/Typography';
import { Button } from 'src/shared/ui/Button';

import { useMutationSendSms } from 'src/dal/services/user/userQueries';
import { getApiError } from 'src/shared/utils/getApiError';

import { ApplicantFormContext, type ApplicantFormValues } from './blocks/ApplicantFormProvider';
import { PolicyConsentText } from './blocks/PolicyConsentText';

import { firstStepValidationSchema } from './validationSchema';
import { AGREEMENT_TYPE_IDS, getAgreementTypeIdsFromConsents, getPreviewDocumentHref, digitsOnly } from './utils';

import styles from '../client-component.module.css';

export const ApplicantForm = () => {
  const searchParams = useSearchParams();

  const { values: savedValues, setValues, setCurrentStep } = useContext(ApplicantFormContext);

  const [initialValues] = useState<ApplicantFormValues>(() => ({
    applicantLastName: savedValues?.applicantLastName ?? '',
    applicantFirstName: savedValues?.applicantFirstName ?? '',
    applicantMiddleName: savedValues?.applicantMiddleName ?? '',
    phone: savedValues?.phone ?? '',
    consentProcessingPersonalData: savedValues?.consentProcessingPersonalData ?? true,
    consentInclusionPersonnelReserve: savedValues?.consentInclusionPersonnelReserve ?? true,
  }));

  const legalId = searchParams.get('legal_id');

  const sendSmsMutation = useMutationSendSms({
    onSuccess: () => {
      setCurrentStep(2);
    },
  });

  const handleSubmit = async (values: ApplicantFormValues, helpers: FormikHelpers<ApplicantFormValues>) => {
    if (!legalId) {
      // TODO: Обработка ошибки отсутствия legalId
      return;
    }

    setValues({
      applicantLastName: values.applicantLastName,
      applicantFirstName: values.applicantFirstName,
      applicantMiddleName: values.applicantMiddleName,
      phone: values.phone,
      consentProcessingPersonalData: values.consentProcessingPersonalData,
      consentInclusionPersonnelReserve: values.consentInclusionPersonnelReserve,
      agreementTypeIds: getAgreementTypeIdsFromConsents({
        consentProcessingPersonalData: values.consentProcessingPersonalData,
        consentInclusionPersonnelReserve: values.consentInclusionPersonnelReserve,
      }),
    });

    try {
      await sendSmsMutation.mutateAsync({
        phone: digitsOnly(values.phone),
        legalId,
      });
    } catch (error) {
      // Ошибка API показывается пользователю под полем телефона и не логируется: это ожидаемый сценарий валидации
      helpers.setFieldError('phone', getApiError(error));
      helpers.setFieldTouched('phone', true, false);
    }
  };

  return (
    <>
      <Typography className={styles.title} view="semibold_24">
        Форма для соискателя
      </Typography>

      <Form<ApplicantFormValues>
        initialValues={initialValues}
        validationSchema={firstStepValidationSchema}
        onSubmit={handleSubmit}
      >
        <Form.Fields>
          <Form.Input
            name="applicantLastName"
            label={
              <Typography view="semibold_14">
                Фамилия{' '}
                <Typography tag="span" view="reg_14">
                  (полное, формируется документ)
                </Typography>
              </Typography>
            }
          />
          <Form.Input
            name="applicantFirstName"
            label={
              <Typography view="semibold_14">
                Имя{' '}
                <Typography tag="span" view="reg_14">
                  (полное, формируется документ)
                </Typography>
              </Typography>
            }
          />
          <Form.Input
            name="applicantMiddleName"
            label={
              <Typography view="semibold_14">
                Отчество{' '}
                <Typography tag="span" view="reg_14">
                  (полное, формируется документ)
                </Typography>
              </Typography>
            }
          />

          <Form.PhoneInput name="phone" label={<Typography view="semibold_14">Номер телефона</Typography>} />

          <PolicyConsentText />

          <Form.Checkbox
            name="consentProcessingPersonalData"
            label={
              <Typography
                className={styles.link}
                target="_blank"
                href={getPreviewDocumentHref(AGREEMENT_TYPE_IDS.processingPersonalData, legalId)}
              >
                Обработка персональных данных
              </Typography>
            }
          />
          <Form.Checkbox
            name="consentInclusionPersonnelReserve"
            label={
              <Typography
                className={styles.link}
                target="_blank"
                href={getPreviewDocumentHref(AGREEMENT_TYPE_IDS.inclusionPersonnelReserve, legalId)}
              >
                Включение в кадровый резерв
              </Typography>
            }
          />
        </Form.Fields>

        <Button type="submit" className={styles.submitButton} stretched disabled={sendSmsMutation.isPending}>
          Согласиться
        </Button>
      </Form>
    </>
  );
};
